import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import StudentsWaitingTable from '@/tables/coordinator/StudentsWaitingTable';
import { useCoordinatorGroupAccess } from '~/hooks/useIsCoordinatorGroup';
import { GroupHeaderWrapper } from '~/styles/layoutElements';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/students/students-waiting',
)({
  // The Study Helpers tab's drill-in, as on the All Students route.
  validateSearch: (search): { study_helper_id?: number } => {
    const id = Number(search.study_helper_id);
    return {
      study_helper_id: Number.isInteger(id) && id > 0 ? id : undefined,
    };
  },
  component: StudentsWaitingScreen,
});

function StudentsWaitingScreen(): ReactElement | null {
  const { t } = useTranslation();
  const { groupName } = Route.useParams();
  const { study_helper_id: deepLinkedHelperId } = Route.useSearch();
  const navigate = useNavigate();

  // Congregations and custom study groups only. Legacy serves this page on a
  // study helper group but never links it, and here its filter panel would 403
  // there, since that list is congregation-scoped.
  //
  // Only the group type is checked; the Students layout has already checked
  // membership. `isResolved` matters: while `getGroupBySlug` loads, a bare
  // `!isCoordinatorGroup` bounces the group's own coordinator.
  const { isCoordinatorGroup, isResolved } = useCoordinatorGroupAccess();
  const refused = isResolved && !isCoordinatorGroup;

  useEffect(() => {
    if (refused) {
      // To All Students, not the group home: the caller passed the Students
      // tab's gate, and All Students is legacy's default subnav.
      void navigate({
        to: '/groups/$groupName/students/all-students/',
        params: { groupName },
        replace: true,
      });
    }
  }, [refused, groupName, navigate]);

  // Seeded once, then stripped, as legacy reads the fragment into the request
  // and clears it.
  const [seededHelperId] = useState(() =>
    deepLinkedHelperId ? [deepLinkedHelperId] : undefined,
  );
  useEffect(() => {
    if (deepLinkedHelperId === undefined) return;
    void navigate({
      to: '/groups/$groupName/students/students-waiting/',
      params: { groupName },
      search: { study_helper_id: undefined },
      replace: true,
    });
  }, [deepLinkedHelperId, groupName, navigate]);

  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const groupId = groupData?.id ?? 0;

  if (refused) return null;

  return (
    <>
      <GroupHeaderWrapper>
        {/* No help placement, as legacy renders a bare heading. */}
        <TitleWithQuestion title={t('lessons.students-waiting')} />
      </GroupHeaderWrapper>
      <StudentsWaitingTable
        groupId={groupId}
        groupType={groupData?.group_type ?? undefined}
        seededHelperId={seededHelperId}
      />
    </>
  );
}
